import {useAuiState} from '@assistant-ui/react';
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown';
import {Link} from '@wandb/weave/common/util/links';
import {MarkdownContainer} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/ChatView/MarkdownContainer';
import katex from 'katex';
import React, {memo} from 'react';
import remarkGfm from 'remark-gfm';

import {CodeSnippet} from '../CodeSnippet';
import {preserveMathDelimiters, remarkMath} from './remarkMath';
import {resolveLinkTarget} from './resolveLinkTarget';
import {sanitizeHref} from './sanitizeHref';

export const Anchor = ({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) => {
  const sanitizedHref = sanitizeHref(href);
  if (!sanitizedHref) {
    return <>{children}</>;
  }

  return (
    <Link
      to={resolveLinkTarget(sanitizedHref)}
      className="font-medium text-teal-600 underline hover:text-teal-500">
      {children}
    </Link>
  );
};

const Code = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => {
  const isCodeBlock = useIsMarkdownCodeBlock();
  if (isCodeBlock) {
    const language = className?.replace(/^language-/, '') ?? 'python';
    return (
      <div className="pb-16">
        <CodeSnippet
          codeSnippet={String(children).replace(/\n$/, '')}
          codeLanguage={language}
        />
      </div>
    );
  }
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const renderMath = (src: string, displayMode: boolean) =>
  katex.renderToString(src, {throwOnError: false, output: 'html', displayMode});

const MathInline = ({children}: {children?: React.ReactNode}) => (
  <span
    dangerouslySetInnerHTML={{
      __html: renderMath(String(children ?? ''), false),
    }}
  />
);

// <span> (not <div>) so the element is valid inside the surrounding <p>.
const MathBlock = ({children}: {children?: React.ReactNode}) => (
  <span
    className="block my-12 overflow-x-auto"
    dangerouslySetInnerHTML={{__html: renderMath(String(children ?? ''), true)}}
  />
);

const assistantMarkdownComponents = memoizeMarkdownComponents({
  a: Anchor,
  pre: ({children}) => <>{children}</>,
  code: Code,
  // Custom tag names emitted by remarkMath
  'math-inline': MathInline,
  'math-block': MathBlock,
} as Parameters<typeof memoizeMarkdownComponents>[0]);

export const MarkdownText = memo(() => {
  const hasText = useAuiState(
    s => s.part.type === 'text' && s.part.text.length > 0
  );
  // Have MarkdownText render null for an empty text part
  // to fix extra space between a ToolCall disclosure row
  // and the DotProgress wave loader. The trailing empty Text
  // component is added by AUI's MessagePrimitive.Parts if
  // the last real part is not text or reasoning
  if (!hasText) {
    return null;
  }
  return (
    <div className="mt-12 first:mt-0">
      <MarkdownContainer>
        <MarkdownTextPrimitive
          remarkPlugins={[remarkGfm, remarkMath]}
          preprocess={preserveMathDelimiters}
          components={assistantMarkdownComponents}
        />
      </MarkdownContainer>
    </div>
  );
});

MarkdownText.displayName = 'MarkdownText';
